import type { AiSearchResult } from '@/shared/api/graphql/aiSearch';
import { useTranslation } from '@/shared/lib/common/i18n';
import useShowMedicalServices from '@/shared/lib/hooks/useShowMedicalServices';
import { MedicalServices } from '@/shared/lib/types/admin';
import { getAccountRedirectUrl } from '@/shared/lib/util/account';
import { formatTime } from '@/shared/lib/util/format';
import { Body1, Small } from '@/shared/ui/components/Typography';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { Theme } from '@mui/material';
import { alpha, Box, Chip, keyframes, Paper, Stack } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ResultCard, TagTone } from '../lib/resultCards';
import { extractCards } from '../lib/resultCards';
import { useAiSearchStore } from '../model/aiSearchStore';

const rise = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

/** Cards come in one after another, but the whole list is in within half a second. */
const STAGGER_MS = 20;

const accentOf = (tags: ResultCard['tags']): TagTone =>
  tags.some(tag => tag.tone === 'error') ? 'error' : tags.some(tag => tag.tone === 'warning') ? 'warning' : 'default';

const paletteColor = (theme: Theme, tone: TagTone) =>
  tone === 'error' ? theme.palette.error.main : tone === 'warning' ? theme.palette.warning.main : theme.palette.primary.main;

type CardProps = { card: ResultCard; index: number; onOpen: (card: ResultCard) => void };

const ResultCardView = ({ card, index, onOpen }: CardProps) => {
  const tone = accentOf(card.tags);
  const clickable = Boolean(card.truentityId);

  return (
    <Paper
      variant="outlined"
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={() => clickable && onOpen(card)}
      onKeyDown={event => clickable && event.key === 'Enter' && onOpen(card)}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        pl: 2.5,
        pr: clickable ? 5 : 2,
        py: 1.5,
        cursor: clickable ? 'pointer' : 'default',
        animation: `${rise} 240ms ease-out both`,
        animationDelay: `${index * STAGGER_MS}ms`,
        transition: 'border-color 150ms, box-shadow 150ms, transform 150ms',
        // The severity of what the card holds, down its left edge.
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '0 auto 0 0',
          width: 4,
          bgcolor: theme => alpha(paletteColor(theme, tone), tone === 'default' ? 0.35 : 0.9)
        },
        ...(clickable && {
          '&:hover, &:focus-visible': {
            borderColor: 'primary.main',
            boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.12)}`,
            transform: 'translateY(-1px)',
            outline: 'none'
          },
          '&:hover .ai-search-card-chevron': { color: 'primary.main', transform: 'translate(2px, -50%)' }
        })
      }}
    >
      <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Body1 sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>{card.title}</Body1>
        {card.date && (
          <Small color="text.secondary" sx={{ flexShrink: 0 }}>
            {formatTime(card.date, 'ddd D MMM, h:mm a')}
          </Small>
        )}
      </Stack>

      {card.subtitle.length > 0 && (
        <Small color="text.secondary" sx={{ display: 'block', mt: 0.25, overflowWrap: 'anywhere' }}>
          {card.subtitle.join(' · ')}
        </Small>
      )}

      {card.tags.length > 0 && (
        <Stack direction="row" useFlexGap spacing={0.75} sx={{ flexWrap: 'wrap', mt: 1 }}>
          {card.tags.map(tag => (
            <Chip
              key={tag.label}
              size="small"
              variant="outlined"
              color={tag.tone === 'default' ? 'default' : tag.tone}
              label={tag.count > 1 ? `${tag.label} ×${tag.count}` : tag.label}
              sx={{ height: 'auto', py: 0.25, '& .MuiChip-label': { whiteSpace: 'normal' } }}
            />
          ))}
        </Stack>
      )}

      {card.details.length > 0 && (
        <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', mt: 1 }}>
          {card.details.map(detail => (
            <Small key={detail.label} color="text.secondary">
              {detail.label}:{' '}
              <Box component="span" sx={{ color: 'text.primary' }}>
                {detail.value}
              </Box>
            </Small>
          ))}
        </Stack>
      )}

      {clickable && (
        <ChevronRightIcon
          className="ai-search-card-chevron"
          sx={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'text.disabled',
            transition: 'all 150ms'
          }}
        />
      )}
    </Paper>
  );
};

/**
 * The results as cards in the answer's own flow: no scroll area of their own, so the dialog has
 * one scroll, and no columns, so any result reads the same way.
 */
const AiSearchResults = ({ result }: { result: AiSearchResult }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setOpen = useAiSearchStore(state => state.setOpen);
  const isRPMViewable = useShowMedicalServices(MedicalServices.RPM);
  const isMTMViewable = useShowMedicalServices(MedicalServices.MTM);

  const { cards, shown, totalCount } = useMemo(() => extractCards(result.data, result.rootField), [result.data, result.rootField]);

  // Same landing-page decision as the header's patient search.
  const openPatient = (card: ResultCard) => {
    if (!card.truentityId) return;
    const service = isRPMViewable ? MedicalServices.RPM : isMTMViewable ? MedicalServices.MTM : MedicalServices.TCM;
    setOpen(false);
    navigate(getAccountRedirectUrl(card.truentityId, service, card.rpmStatus).replace(/^(\.\.\/)+/, '/'));
  };

  // A total answers "how many" even when no rows came back with it.
  const total = totalCount !== null && totalCount > 0 ? totalCount : null;
  const totalLine = total !== null && <Body1 sx={{ fontWeight: 600 }}>{t.aiSearch.answer.total(total)}</Body1>;

  if (cards.length === 0) return totalLine || <Small color="text.secondary">{t.aiSearch.answer.noResults}</Small>;

  const listed = total === null ? t.aiSearch.answer.resultCount(shown, totalCount) : total > shown && t.aiSearch.answer.showingFirst(shown);
  const grouped = cards.length < shown && t.aiSearch.answer.groupedInto(cards.length);
  const note = [listed, grouped].filter(Boolean).join(' · ');

  return (
    <Stack spacing={1}>
      {totalLine}
      {note && <Small color="text.secondary">{note}</Small>}
      {cards.map((card, index) => (
        <ResultCardView key={card.key} card={card} index={index} onOpen={openPatient} />
      ))}
    </Stack>
  );
};

export default AiSearchResults;
